import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { TermsDialog } from "../components/Terms";
import { toast } from "sonner";
import { Coins, Loader2, Check } from "lucide-react";

export default function CreditsPage() {
  const { user } = useAuth();
  const { t, translateError } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  const buy = async (pkgId) => {
    if (!accepted) {
      toast.error(t("credits.termsRequired"));
      return;
    }
    setLoadingId(pkgId);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
      setLoadingId(null);
    }
  };

  return (
    <main className="relative z-10 mx-auto max-w-5xl px-6 py-16 md:px-10">
      <div className="mb-4 flex items-center gap-3">
        <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("credits.eyebrow")}</span>
      </div>
      <h1 className="font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("credits.title")}</h1>
      <div className="mt-4 flex items-center gap-2 text-lg text-muted-foreground">
        <Coins className="h-5 w-5 text-credit" />
        {t("credits.current")}{" "}
        <span className="font-heading font-bold text-credit" data-testid="credits-balance">
          {user?.role === "admin" ? "∞" : user?.credits ?? 0}
        </span>{" "}
        {t("common.credits")}
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {packages.map((p, i) => (
          <div
            key={p.id}
            data-testid={`package-${p.id}`}
            className={`relative flex flex-col border bg-card p-8 transition-transform hover:-translate-y-1 ${
              i === 1 ? "border-primary" : "border-white/10 hover:border-white/30"
            }`}
          >
            {i === 1 && (
              <span className="absolute -top-3 left-8 bg-primary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
                {t("credits.popular")}
              </span>
            )}
            <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-heading text-5xl font-black text-credit">{p.credits}</span>
              <span className="text-sm text-muted-foreground">{t("common.credits")}</span>
            </div>
            <p className="mt-2 font-mono text-sm text-muted-foreground">€ {p.amount.toFixed(2)}</p>
            <ul className="mt-6 flex-1 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-truth" /> {t("credits.available")}</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-truth" /> {t("credits.betOnAny")}</li>
            </ul>
            <Button
              onClick={() => buy(p.id)}
              disabled={loadingId !== null || !accepted}
              data-testid={`buy-${p.id}`}
              className="mt-8 w-full rounded-md bg-primary py-6 font-bold uppercase tracking-wider hover:bg-[#FF645A] disabled:opacity-40"
            >
              {loadingId === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : t("credits.buy")}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-start gap-3 border border-white/10 bg-card p-4" data-testid="terms-agree-box">
        <Checkbox id="terms" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="terms-checkbox" className="mt-0.5" />
        <label htmlFor="terms" className="cursor-pointer text-sm text-foreground/85">
          {t("credits.termsPre")}{" "}
          <button type="button" onClick={(e) => { e.preventDefault(); setTermsOpen(true); }} data-testid="terms-link" className="text-credit underline underline-offset-4 hover:text-credit/80">
            {t("credits.termsLink")}
          </button>
          .
        </label>
      </div>

      <p className="mt-6 max-w-2xl text-xs text-muted-foreground">{t("credits.disclaimer")}</p>

      <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} />
    </main>
  );
}
